import { useEffect, useState } from "react";
import { Check, CreditCard, Flame, Loader2, Settings, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, dateIt, dateLong } from "@/lib/api";
import { money, pick } from "@/lib/pricing";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";

const PLAN_BADGE = { pro: "PRO", pass: "LAUNCH PASS", trial: "TRIAL", beta: "BETA", free: "FREE" };

const Abbonamento = () => {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState("");
  const [reserving, setReserving] = useState(false);
  const [seat, setSeat] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    api.get("/subscription").then(({ data }) => setSub(data));
  }, []);

  const checkout = async (lookupKey) => {
    setLoading(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setLoading("");
    }
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.post("/payments/portal", {
        origin_url: window.location.origin,
      });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setPortalLoading(false);
    }
  };

  const reserveSeat = async () => {
    setReserving(true);
    try {
      const { data } = await api.post("/public/reserve", { email: user.email, source: "app" });
      setSeat(data.seat_no);
      toast.success(t("plan.reserveDone"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setReserving(false);
    }
  };

  if (!sub) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;

  const prebook = sub.sales_mode === "prebook";
  const isPro = sub.plan === "pro";
  const isPass = sub.plan === "pass";
  const isPaid = isPro || isPass;
  const benefits = [t("plan.b1"), t("plan.b2"), t("plan.b3"), t("plan.b4"), t("plan.b5")];
  const seatsLeft = sub.pass_seats_left ?? 0;
  const passPrice = pick(lang, sub, "pass_price_usd", "pass_price_eur", 49.9, 49.9);
  const proPrice = pick(lang, sub, "price_usd", "price_eur", 14.9, 14.9);
  const yearPrice = pick(lang, sub, "year_price_usd", "year_price_eur", 149, 149);

  return (
    <div className="max-w-3xl space-y-6" data-testid="subscription-page">
      <div className="card-flat p-6" data-testid="current-plan">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t("plan.current")}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <span
            className={`rounded px-2.5 py-1 text-sm font-bold ${
              isPaid ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-700"
            }`}
          >
            {PLAN_BADGE[sub.plan] || "FREE"}
          </span>
          {sub.status === "trialing" && (
            <span className="text-sm text-slate-500">{t("plan.trialing")}</span>
          )}
          {sub.plan === "trial" && (
            <span className="text-sm text-slate-500" data-testid="trial-status">
              {sub.trial_days_left} {t("plan.trialDaysLeft")}
            </span>
          )}
          {isPass && (
            <span className="text-sm text-slate-500" data-testid="pass-status">
              {t("plan.passActive")} · {t("plan.passExpires")}{" "}
              {dateLong(sub.pass_expires_at)} ({sub.pass_days_left} {t("plan.passDaysLeft")})
            </span>
          )}
          {sub.current_period_end && (
            <span className="font-mono-num text-sm text-slate-500">
              {t("plan.renews")} {dateIt(new Date(sub.current_period_end * 1000).toISOString())}
            </span>
          )}
        </div>
        {sub.doc_limit && (
          <p className="mt-3 text-sm text-slate-600">
            {t("plan.usage")}: <b className="font-mono-num">{sub.documents_this_month}</b> /{" "}
            {sub.doc_limit} {t("plan.docsMonth")}
          </p>
        )}
        {sub.ai_credit_limit && (
          <p className="mt-3 text-sm text-slate-600" data-testid="ai-credits">
            {t("plan.aiUsage")}: <b className="font-mono-num">{sub.ai_credits_used}</b> /{" "}
            {sub.ai_credit_limit} {t("plan.aiMonth")}
            {sub.ai_bonus > 0 && (
              <span className="ml-1.5 text-emerald-600" data-testid="ai-bonus">
                (+{sub.ai_bonus} {t("plan.waitlistBonus")})
              </span>
            )}
          </p>
        )}
        {isPro && (
          <div className="mt-4">
            <Button
              data-testid="manage-subscription-button"
              onClick={openPortal}
              disabled={portalLoading}
              variant="outline"
              className="gap-2"
            >
              {portalLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Settings className="h-4 w-4" />
              )}
              {t("plan.manage")}
            </Button>
            <p className="mt-2 text-xs text-slate-500">{t("plan.manageHint")}</p>
          </div>
        )}
        {isPass && (
          <p className="mt-4 text-xs text-slate-500" data-testid="pass-no-renewal">
            {t("plan.passNoRenewal")}
          </p>
        )}
      </div>

      {prebook && !isPaid && (
        <div
          className="card-flat relative overflow-hidden border-indigo-500/30 bg-slate-950 p-8 text-slate-100 grain"
          data-testid="prebook-card"
        >
          <div className="relative">
            <span className="inline-block rounded-full bg-indigo-500/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-300">
              {t("plan.prebookBadge")}
            </span>
            <p className="mt-5 flex items-center gap-2 text-sm font-semibold text-indigo-300">
              <Sparkles className="h-4 w-4" /> {t("plan.passName")}
            </p>
            <p className="font-mono-num mt-3 text-5xl font-semibold">
              {money(lang, passPrice)}{" "}
              <span className="text-base font-normal text-slate-400">{t("plan.oneTime")}</span>
            </p>
            <p className="mt-4 text-sm leading-relaxed text-slate-300">{t("plan.prebookText")}</p>
            <p
              className="mt-5 flex items-center gap-2 text-sm font-semibold text-emerald-400"
              data-testid="prebook-seats"
            >
              <Flame className="h-4 w-4" />
              {seatsLeft > 0 ? `${seatsLeft} ${t("plan.seatsLeft")}` : t("plan.soldOut")}
            </p>
            <Button
              data-testid="reserve-seat-button"
              onClick={reserveSeat}
              disabled={reserving || seat !== null || sub.pass_sold_out}
              className="mt-6 w-full gap-2 bg-indigo-600 py-6 text-base text-white hover:bg-indigo-500 sm:w-auto sm:px-8"
            >
              {reserving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Flame className="h-4 w-4" />
              )}
              {seat !== null ? `${t("plan.seatNo")} #${seat}` : t("plan.reserveCta")}
            </Button>
            <p className="mt-4 text-xs text-slate-400">{t("plan.prebookNote")}</p>
            <p className="mt-2 text-xs text-slate-400" data-testid="prebook-after-price">
              {t("plan.afterPrice").replace("{price}", money(lang, proPrice))}
            </p>
          </div>
        </div>
      )}

      {!prebook && !isPaid && (
        <div
          className="card-flat relative overflow-hidden border-indigo-500/30 bg-slate-950 p-8 text-slate-100 grain"
          data-testid="pass-card"
        >
          <div className="relative">
            <span className="inline-block rounded-full bg-indigo-500/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-indigo-300">
              {t("plan.founderBadge")}
            </span>
            <p className="mt-5 flex items-center gap-2 text-sm font-semibold text-indigo-300">
              <Sparkles className="h-4 w-4" /> {t("plan.passName")}
            </p>
            <p className="font-mono-num mt-3 text-5xl font-semibold">
              {money(lang, passPrice)}{" "}
              <span className="text-base font-normal text-slate-400">{t("plan.oneTime")}</span>
            </p>
            <p className="mt-4 text-sm leading-relaxed text-slate-300">{t("plan.passDesc")}</p>
            <p
              className="mt-5 flex items-center gap-2 text-sm font-semibold text-emerald-400"
              data-testid="pass-seats"
            >
              <Flame className="h-4 w-4" />
              {seatsLeft > 0 ? `${seatsLeft} ${t("plan.seatsLeft")}` : t("plan.soldOut")}
            </p>
            <Button
              data-testid="buy-pass-button"
              onClick={() => checkout(sub.pass_lookup_key)}
              disabled={loading === sub.pass_lookup_key || sub.pass_sold_out}
              className="mt-6 w-full gap-2 bg-indigo-600 py-6 text-base text-white hover:bg-indigo-500 sm:w-auto sm:px-8"
            >
              {loading === sub.pass_lookup_key ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CreditCard className="h-4 w-4" />
              )}
              {t("plan.passCta")}
            </Button>
            <p className="mt-4 text-xs text-slate-400">{t("plan.guarantee")}</p>
            <p className="mt-1.5 text-xs text-slate-500">{t("plan.fairUse")}</p>
          </div>
        </div>
      )}

      {!prebook && !isPro && (
        <div className="card-flat p-8" data-testid="pro-card">
          <p className="text-sm font-semibold text-slate-500">{t("plan.proName")}</p>
          <p className="font-mono-num mt-3 text-5xl font-semibold">
            {money(lang, proPrice)}{" "}
            <span className="text-base font-normal text-slate-500">{t("plan.perMonth")}</span>
          </p>
          <p className="mt-3 text-sm text-slate-600">{t("plan.trial")}</p>
          <ul className="mt-6 space-y-2.5">
            {benefits.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                {b}
              </li>
            ))}
          </ul>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Button
              data-testid="subscribe-button"
              onClick={() => checkout("pro_monthly_usd")}
              disabled={loading === "pro_monthly_usd"}
              variant="outline"
              className="w-full gap-2 border-slate-900 py-6 text-base font-semibold hover:bg-slate-900 hover:text-white sm:w-auto sm:px-8"
            >
              {loading === "pro_monthly_usd" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CreditCard className="h-4 w-4" />
              )}
              {t("plan.cta")}
            </Button>
            <Button
              data-testid="subscribe-yearly-button"
              onClick={() => checkout(sub.year_lookup_key)}
              disabled={loading === sub.year_lookup_key}
              variant="outline"
              className="w-full gap-2 border-slate-300 py-6 text-base font-semibold text-slate-800 hover:bg-slate-100 sm:w-auto sm:px-6"
            >
              {loading === sub.year_lookup_key ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {money(lang, yearPrice)} {t("plan.year")}
            </Button>
          </div>
          <p className="mt-3 text-xs font-semibold text-emerald-700" data-testid="year-save">
            {t("plan.yearSave")}
          </p>
          <p className="mt-2 text-xs text-slate-500">{t("plan.testCard")}</p>
        </div>
      )}
    </div>
  );
};

export default Abbonamento;
